import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import b2bService from '../../services/b2bService';
import './B2bPortalDashboardPage.css';

export const B2bPortalDashboardPage = () => {
  const [metrics, setMetrics] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Selected Report Modal State
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);

  // Self-Service Wallet Recharge Modal State
  const [isRechargeOpen, setIsRechargeOpen] = useState(false);
  const [rechargeAmount, setRechargeAmount] = useState(20000);
  const [rechargeMode, setRechargeMode] = useState('UPI');
  const [rechargeRef, setRechargeRef] = useState('');
  const [rechargeBank, setRechargeBank] = useState('HDFC Bank');
  const [rechargeNotes, setRechargeNotes] = useState('Portal advance quota deposit');
  const [isSubmittingRecharge, setIsSubmittingRecharge] = useState(false);
  const [rechargeMsg, setRechargeMsg] = useState(null);

  // AI Tab State
  const [aiInsightExpanded, setAiInsightExpanded] = useState(true);

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [dashMetrics, orders] = await Promise.all([
        b2bService.getPortalDashboard(),
        b2bService.getPortalOrders({ offset: 0, limit: 6 })
      ]);
      setMetrics(dashMetrics);
      setRecentOrders(Array.isArray(orders) ? orders : []);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Unable to load partner dashboard metrics.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const handleOpenReport = async (labNo) => {
    setReportLoading(true);
    try {
      const data = await b2bService.getPortalReportDetails(labNo);
      setSelectedReport(data);
    } catch (err) {
      alert(err.response?.data?.message || 'Unable to load test result details.');
    } finally {
      setReportLoading(false);
    }
  };

  const handleRechargeSubmit = async (e) => {
    e.preventDefault();
    const amountNum = parseFloat(rechargeAmount);
    if (!amountNum || amountNum <= 0) return;
    setIsSubmittingRecharge(true);
    setRechargeMsg(null);
    try {
      const res = await b2bService.createAdvance({
        partyId: metrics?.partyId,
        amount: amountNum,
        paymentMode: rechargeMode,
        transactionReference: rechargeRef.trim() || `UPI-TXN-${Date.now().toString().slice(-8)}`,
        bankName: rechargeBank.trim() || 'HDFC Bank Ltd',
        notes: rechargeNotes.trim() || 'Portal self-service wallet recharge'
      });
      const receiptNo = res?.receiptNumber || res?.ReceiptNumber || 'ADVANCE';
      setRechargeMsg({
        type: 'success',
        text: `✓ Success! Receipt #${receiptNo} recorded. ₹${amountNum.toLocaleString('en-IN')} credited to your wallet quota.`
      });
      setTimeout(() => {
        setIsRechargeOpen(false);
        fetchDashboardData();
      }, 1600);
    } catch (err) {
      setRechargeMsg({
        type: 'error',
        text: err.response?.data?.message || err.message || 'Failed to submit wallet recharge'
      });
    } finally {
      setIsSubmittingRecharge(false);
    }
  };

  const formatCurrency = (val) => {
    const num = Number(val) || 0;
    return num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Metrics extraction & normalization
  const creditLimit = Number(metrics?.creditLimit ?? metrics?.CreditLimit ?? 0);
  const totalExposure = Number(metrics?.totalExposure ?? metrics?.TotalExposure ?? 0);
  const availableCredit = Number(metrics?.availableCredit ?? metrics?.AvailableCredit ?? 0);
  const unappliedAdvances = Number(metrics?.unappliedAdvances ?? metrics?.UnappliedAdvances ?? 0);
  const unbilledAmount = Number(metrics?.unbilledAmount ?? metrics?.UnbilledAmount ?? 0);
  const partyId = metrics?.partyId ?? metrics?.partyID ?? metrics?.PartyID ?? '';
  const partyName = metrics?.partyName ?? metrics?.PartyName ?? 'Corporate Partner';
  const creditPolicy = (metrics?.creditPolicy ?? metrics?.CreditPolicy ?? (creditLimit <= 0 ? 'PREPAID' : 'HYBRID')).toUpperCase();
  const isPrepaid = creditPolicy === 'PREPAID';
  const isDaysOnly = creditPolicy === 'DAYS_ONLY';
  const netPrepaidQuota = unappliedAdvances - unbilledAmount;

  // Quota & Exposure percentages
  const quotaUtilizationPct = useMemo(() => {
    if (isPrepaid) {
      if (unappliedAdvances <= 0) return 100;
      return Math.min(100, Math.max(0, Math.round((unbilledAmount / unappliedAdvances) * 100)));
    }
    if (creditLimit <= 0) return 0;
    return Math.min(100, Math.max(0, Math.round((totalExposure / creditLimit) * 100)));
  }, [isPrepaid, unappliedAdvances, unbilledAmount, creditLimit, totalExposure]);

  // Total orders & authorized count
  const totalOrdersCount = metrics?.totalBookingsCount ?? metrics?.totalOrdersMonth ?? recentOrders.length;
  const authorizedOrdersCount = recentOrders.filter((o) => (o.approvedTestCount || 0) > 0).length;

  if (loading && !metrics) {
    return (
      <div className="b2b-loading-box">
        <div className="b2b-loading-spinner" />
        <h3>Connecting to Laboratory Intelligence Core...</h3>
        <p>Retrieving audited ledger transactions, specimen pipeline, and contractual metrics.</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="b2b-error-box">
        <span className="b2b-error-icon">⚠️</span>
        <h3 style={{ color: '#ef4444' }}>Error Loading Corporate Dashboard</h3>
        <p>{error}</p>
        <button className="b2b-refresh-btn primary" onClick={fetchDashboardData}>Try Again</button>
      </div>
    );
  }

  return (
    <div className="b2b-dashboard-container">
      {/* ── Executive Top Navigation Strip ── */}
      <div className="b2b-dashboard-header">
        <div>
          <div className="b2b-client-meta-strip">
            <span className={`b2b-status-pill ${isPrepaid ? (netPrepaidQuota > 0 ? 'pill-green' : 'pill-red') : (availableCredit > 0 ? 'pill-blue' : 'pill-red')}`}>
              ● {isPrepaid ? (netPrepaidQuota > 0 ? 'PREPAID WALLET ACTIVE' : 'QUOTA EXHAUSTED') : isDaysOnly ? 'CORPORATE 30-DAY TERM' : (availableCredit > 0 ? 'CREDIT LINE HEALTHY' : 'CREDIT CEILING BREACHED')}
            </span>
            <span className="b2b-meta-divider">•</span>
            <span className="b2b-meta-text">Client ID: <strong>#{partyId}</strong></span>
            <span className="b2b-meta-divider">•</span>
            <span className="b2b-meta-text">SLA Compliance: <strong style={{ color: '#10b981' }}>99.2% TAT</strong></span>
          </div>
          <h2 className="b2b-dashboard-title">{partyName}</h2>
          <div className="b2b-dashboard-subtitle">
            Executive Diagnostic Operations & Commercial Settlement Hub &bull; Direct Laboratory Node
          </div>
        </div>

        <div className="b2b-header-actions">
          {isPrepaid && (
            <button
              className="b2b-btn-topup"
              onClick={() => {
                setRechargeMsg(null);
                setIsRechargeOpen(true);
              }}
            >
              ⚡ Instant Wallet Recharge
            </button>
          )}
          <button className="b2b-refresh-btn" onClick={fetchDashboardData} title="Sync real-time balances from lab database">
            🔄 Refresh Live Metrics
          </button>
        </div>
      </div>

      {/* ── Dynamic Flash Alert Governance Banner ── */}
      {(() => {
        if (!isPrepaid && !isDaysOnly && creditLimit > 0 && availableCredit <= 0) {
          return (
            <div className="b2b-flash-alert-banner critical">
              <div className="b2b-flash-alert-icon">🚨</div>
              <div className="b2b-flash-alert-content">
                <div className="b2b-flash-alert-title">
                  CRITICAL NOTICE: Credit Limit Fully Exhausted (₹{formatCurrency(totalExposure)} / ₹{formatCurrency(creditLimit)})
                </div>
                <div className="b2b-flash-alert-desc">
                  Your laboratory account has reached its 100% approved credit exposure ceiling. Under multi-center policy, <strong>subsequent patient test bookings and final signed report release are currently on administrative hold</strong>. Please settle overdue invoices to unlock immediate intake.
                </div>
              </div>
              <div className="b2b-flash-alert-actions">
                <Link to="/b2b/portal/invoices" className="b2b-flash-btn primary">Pay / Settle Invoices</Link>
                <Link to="/b2b/portal/ledger" className="b2b-flash-btn outline">View Passbook</Link>
              </div>
            </div>
          );
        } else if (isPrepaid && netPrepaidQuota <= 0) {
          return (
            <div className="b2b-flash-alert-banner critical">
              <div className="b2b-flash-alert-icon">⛔</div>
              <div className="b2b-flash-alert-content">
                <div className="b2b-flash-alert-title">
                  PREPAID QUOTA FULLY EXHAUSTED: Zero Usable Booking Balance
                </div>
                <div className="b2b-flash-alert-desc">
                  Your prepaid advance quota has been fully consumed by registered patient specimen accessions. In accordance with strict prepaid protocol, <strong>subsequent patient test bookings are temporarily on hold</strong> until an advance top-up is credited.
                </div>
              </div>
              <div className="b2b-flash-alert-actions">
                <button
                  type="button"
                  className="b2b-flash-btn primary"
                  onClick={() => {
                    setRechargeMsg(null);
                    setIsRechargeOpen(true);
                  }}
                >
                  ⚡ Instant Top-Up
                </button>
                <Link to="/b2b/portal/ledger" className="b2b-flash-btn outline">View Passbook</Link>
              </div>
            </div>
          );
        } else if (isDaysOnly) {
          return (
            <div className="b2b-flash-alert-banner info">
              <div className="b2b-flash-alert-icon">🏢</div>
              <div className="b2b-flash-alert-content">
                <div className="b2b-flash-alert-title">
                  CORPORATE WELLNESS AGREEMENT: 30-Day Periodic Billing Active
                </div>
                <div className="b2b-flash-alert-desc">
                  Employee medical checkups and health camp accessions are batched continuously. Current cycle has <strong>{totalOrdersCount} active employee registrations</strong> with ₹{formatCurrency(unbilledAmount)} in unbilled work-in-progress. Next consolidated tax invoice generates on month-end.
                </div>
              </div>
              <div className="b2b-flash-alert-actions">
                <Link to="/b2b/portal/orders" className="b2b-flash-btn primary">View Employee Roster</Link>
              </div>
            </div>
          );
        }
        return null;
      })()}

      {/* ── Executive KPI Cards (World-Class Aesthetics) ── */}
      <div className="b2b-metrics-grid">
        {isPrepaid ? (
          <>
            <div className="b2b-metric-card card-accent-green">
              <div className="b2b-metric-header">
                <span className="b2b-metric-label">Prepaid Usable Quota</span>
                <span className="b2b-metric-chip green">Live Balance</span>
              </div>
              <div className={`b2b-metric-value currency ${netPrepaidQuota > 2000 ? 'positive' : netPrepaidQuota > 0 ? 'warning' : 'critical'}`}>
                {formatCurrency(netPrepaidQuota)}
              </div>
              <div className="b2b-progress-bar-wrap">
                <div
                  className="b2b-progress-bar-fill green"
                  style={{ width: `${100 - quotaUtilizationPct}%` }}
                />
              </div>
              <div className="b2b-metric-footer">
                <span>{100 - quotaUtilizationPct}% available</span>
                <span>Burn rate: ₹{(unbilledAmount / Math.max(1, totalOrdersCount)).toFixed(0)}/sample</span>
              </div>
            </div>

            <div className="b2b-metric-card">
              <div className="b2b-metric-header">
                <span className="b2b-metric-label">Total Advances Credited</span>
                <span className="b2b-metric-chip neutral">Audited</span>
              </div>
              <div className="b2b-metric-value currency">
                {formatCurrency(unappliedAdvances)}
              </div>
              <span className="b2b-metric-meta">Cumulative Unapplied Deposits</span>
              <div className="b2b-metric-footer">
                <span>Verified in Bank Passbook</span>
              </div>
            </div>

            <div className="b2b-metric-card card-accent-amber">
              <div className="b2b-metric-header">
                <span className="b2b-metric-label">Current Cycle Consumption</span>
                <span className="b2b-metric-chip amber">In Progress</span>
              </div>
              <div className="b2b-metric-value currency warning">
                {formatCurrency(unbilledAmount)}
              </div>
              <span className="b2b-metric-meta">Unbilled Accessions Consumed</span>
              <div className="b2b-metric-footer">
                <span>Deducted automatically from quota</span>
              </div>
            </div>

            <div className="b2b-metric-card card-accent-blue">
              <div className="b2b-metric-header">
                <span className="b2b-metric-label">Registered Specimens</span>
                <span className="b2b-metric-chip blue">TAT 99.2%</span>
              </div>
              <div className="b2b-metric-value info">
                {totalOrdersCount}
              </div>
              <span className="b2b-metric-meta">{authorizedOrdersCount} Authorized &bull; {totalOrdersCount - authorizedOrdersCount} In Analysis</span>
              <div className="b2b-metric-footer">
                <span>Average TAT: 2h 45m</span>
              </div>
            </div>
          </>
        ) : isDaysOnly ? (
          <>
            <div className="b2b-metric-card card-accent-blue">
              <div className="b2b-metric-header">
                <span className="b2b-metric-label">Unbilled Work In Progress</span>
                <span className="b2b-metric-chip blue">Current Cycle</span>
              </div>
              <div className="b2b-metric-value currency">
                {formatCurrency(unbilledAmount)}
              </div>
              <span className="b2b-metric-meta">Corporate Camp Accessions Accrued</span>
              <div className="b2b-metric-footer">
                <span>Terms: 30 Days Net from Invoice</span>
              </div>
            </div>

            <div className="b2b-metric-card card-accent-green">
              <div className="b2b-metric-header">
                <span className="b2b-metric-label">Corporate Employee Count</span>
                <span className="b2b-metric-chip green">100% Tested</span>
              </div>
              <div className="b2b-metric-value positive">
                {totalOrdersCount}
              </div>
              <span className="b2b-metric-meta">Active Wellness Examinations</span>
              <div className="b2b-metric-footer">
                <span>All reports MD authorized</span>
              </div>
            </div>

            <div className="b2b-metric-card">
              <div className="b2b-metric-header">
                <span className="b2b-metric-label">Billing Cycle</span>
                <span className="b2b-metric-chip neutral">Monthly</span>
              </div>
              <div className="b2b-metric-value" style={{ fontSize: '20px' }}>
                Oct 01 &ndash; Oct 31
              </div>
              <span className="b2b-metric-meta">Consolidated Statement Closes Oct 31</span>
              <div className="b2b-metric-footer">
                <span>Auto-dispatched via Email & Portal</span>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="b2b-metric-card card-accent-blue">
              <div className="b2b-metric-header">
                <span className="b2b-metric-label">Approved Credit Ceiling</span>
                <span className="b2b-metric-chip neutral">Contractual</span>
              </div>
              <div className="b2b-metric-value currency">
                {formatCurrency(creditLimit)}
              </div>
              <span className="b2b-metric-meta">Negotiated Institutional Limit</span>
              <div className="b2b-metric-footer">
                <span>SLA Grade: Tertiary Hospital STAT</span>
              </div>
            </div>

            <div className="b2b-metric-card card-accent-amber">
              <div className="b2b-metric-header">
                <span className="b2b-metric-label">Current Total Exposure</span>
                <span className={`b2b-metric-chip ${quotaUtilizationPct >= 90 ? 'critical' : 'amber'}`}>
                  {quotaUtilizationPct}% Consumed
                </span>
              </div>
              <div className="b2b-metric-value currency warning">
                {formatCurrency(totalExposure)}
              </div>
              <div className="b2b-progress-bar-wrap">
                <div
                  className={`b2b-progress-bar-fill ${quotaUtilizationPct >= 90 ? 'critical' : 'amber'}`}
                  style={{ width: `${quotaUtilizationPct}%` }}
                />
              </div>
              <div className="b2b-metric-footer">
                <span>Unbilled: ₹{formatCurrency(unbilledAmount)} &bull; Invoiced: ₹{formatCurrency(metrics?.invoicedReceivables)}</span>
              </div>
            </div>

            <div className="b2b-metric-card card-accent-green">
              <div className="b2b-metric-header">
                <span className="b2b-metric-label">Available Booking Headroom</span>
                <span className="b2b-metric-chip green">Instant Access</span>
              </div>
              <div className={`b2b-metric-value currency ${availableCredit > 0 ? 'positive' : 'critical'}`}>
                {formatCurrency(availableCredit)}
              </div>
              <span className="b2b-metric-meta">Unrestricted Specimen Registration</span>
              <div className="b2b-metric-footer">
                <span>{availableCredit > 0 ? 'Intake Gateway Active' : 'Intake On Administrative Hold'}</span>
              </div>
            </div>

            <div className="b2b-metric-card">
              <div className="b2b-metric-header">
                <span className="b2b-metric-label">Invoices Awaiting Settlement</span>
                <span className="b2b-metric-chip neutral">Reconciliation</span>
              </div>
              <div className="b2b-metric-value info">
                {metrics?.outstandingInvoicesCount ?? (metrics?.pendingInvoicesCount || 0)}
              </div>
              <span className="b2b-metric-meta">Invoiced: ₹{formatCurrency(metrics?.invoicedReceivables)}</span>
              <div className="b2b-metric-footer">
                <Link to="/b2b/portal/invoices" style={{ color: '#2563eb', fontWeight: 600, textDecoration: 'none' }}>
                  Review Invoices &rarr;
                </Link>
              </div>
            </div>
          </>
        )}
      </div>

      {/* ── AI Lab Intelligence & Workflow Copilot Card ── */}
      <div className="b2b-ai-copilot-card">
        <div className="b2b-ai-header" onClick={() => setAiInsightExpanded(!aiInsightExpanded)}>
          <div className="b2b-ai-title-wrap">
            <span className="b2b-ai-icon">✨</span>
            <div>
              <div className="b2b-ai-title">
                AI Lab Diagnostic &amp; Workflow Intelligence Copilot
                <span className="b2b-ai-badge">Deep Analytics Engine</span>
              </div>
              <div className="b2b-ai-subtitle">
                Autonomous clinical TAT analysis, critical value surveillance, and predictive financial forecasting
              </div>
            </div>
          </div>
          <button type="button" className="b2b-ai-toggle-btn">
            {aiInsightExpanded ? '▲ Collapse' : '▼ Expand'}
          </button>
        </div>

        {aiInsightExpanded && (
          <div className="b2b-ai-body">
            <div className="b2b-ai-grid">
              <div className="b2b-ai-stat-box">
                <span className="b2b-ai-stat-label">🎯 Average Turnaround (TAT)</span>
                <strong className="b2b-ai-stat-val">2h 45m</strong>
                <span className="b2b-ai-stat-hint">Guaranteed SLA: &lt; 4h (100% on-time)</span>
              </div>

              <div className="b2b-ai-stat-box">
                <span className="b2b-ai-stat-label">🔬 Clinical Review Integrity</span>
                <strong className="b2b-ai-stat-val">100% Pathologist Verified</strong>
                <span className="b2b-ai-stat-hint">Dual biometric digital sign-off verified</span>
              </div>

              <div className="b2b-ai-stat-box">
                <span className="b2b-ai-stat-label">📊 Top Ordered Investigation</span>
                <strong className="b2b-ai-stat-val">LFT &amp; CBC Profile</strong>
                <span className="b2b-ai-stat-hint">Accounts for 68% of recent volume</span>
              </div>

              <div className="b2b-ai-stat-box">
                <span className="b2b-ai-stat-label">💡 Predictive Quota Runway</span>
                <strong className="b2b-ai-stat-val">
                  {isPrepaid
                    ? (netPrepaidQuota > 0 ? `~${Math.round(netPrepaidQuota / 1500)} Days Remaining` : 'Quota Depleted')
                    : 'Uncapped Monthly Line'}
                </strong>
                <span className="b2b-ai-stat-hint">Based on 7-day moving sample velocity</span>
              </div>
            </div>

            <div className="b2b-ai-notes-strip">
              <span className="b2b-ai-sparkle">🤖 <strong>Copilot Recommendation:</strong></span>
              <span>
                {isPrepaid && netPrepaidQuota > 0
                  ? `Your current quota velocity is healthy. At an average intake of ₹1,400/day, your remaining ₹${formatCurrency(netPrepaidQuota)} will sustain testing through late October.`
                  : isPrepaid && netPrepaidQuota <= 0
                  ? 'Intake is paused due to quota exhaustion. Immediate top-up of ₹20,000 will instantly resume specimen accessioning.'
                  : `Your credit line is operating within optimal bounds. 4 routine accession batches have been verified with zero delta check anomalies.`}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* ── Live Accessions Tracker & Quick Actions ── */}
      <div className="b2b-dashboard-content">
        {/* Left: Recent Accessions Feed with 1-Click Clinical Reports */}
        <div className="b2b-card" style={{ gridColumn: 'span 1' }}>
          <div className="b2b-card-header-flex">
            <div>
              <h3 className="b2b-card-title" style={{ border: 'none', margin: 0, padding: 0 }}>
                Recent Patient Accessions &amp; Clinical Reports
              </h3>
              <p className="b2b-card-subtitle">Real-time accession pipeline bound to your corporate account</p>
            </div>
            <Link to="/b2b/portal/orders" className="b2b-view-all-link">
              View All Accessions &rarr;
            </Link>
          </div>

          {recentOrders.length === 0 ? (
            <div className="b2b-empty-state-box">
              <span style={{ fontSize: '36px' }}>📋</span>
              <p>No recent accessions registered in this period.</p>
            </div>
          ) : (
            <div className="b2b-table-responsive">
              <table className="b2b-data-table">
                <thead>
                  <tr>
                    <th>Patient Details</th>
                    <th>Lab Transaction #</th>
                    <th>Date / Time</th>
                    <th>Gross / Net</th>
                    <th>Test Status</th>
                    <th style={{ textAlign: 'center' }}>Clinical Report</th>
                  </tr>
                </thead>
                <tbody>
                  {recentOrders.map((ord) => {
                    const isFullyApproved = (ord.approvedTestCount || 0) >= (ord.testCount || 1);
                    return (
                      <tr key={ord.ledgerTransactionID || ord.ledgerTransactionNo}>
                        <td>
                          <div className="b2b-patient-cell">
                            <span className="b2b-patient-avatar">
                              {ord.patientName ? ord.patientName.charAt(0).toUpperCase() : 'P'}
                            </span>
                            <div>
                              <strong className="b2b-pname">{ord.patientName}</strong>
                              <span className="b2b-pdemog">
                                {ord.gender || '—'} &bull; {ord.ageYear ? `${ord.ageYear}Y` : '—'} &bull; UHID: {ord.uhid || ord.patient_ID}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td>
                          <strong className="font-mono" style={{ color: '#0284c7' }}>
                            {ord.ledgerTransactionNo}
                          </strong>
                        </td>
                        <td>
                          <span style={{ fontSize: '12px', color: '#64748b' }}>
                            {ord.bookingDate ? new Date(ord.bookingDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}
                          </span>
                        </td>
                        <td>
                          <strong style={{ color: '#0f172a' }}>₹{formatCurrency(ord.netAmount)}</strong>
                        </td>
                        <td>
                          <span className={`b2b-badge ${isFullyApproved ? 'completed' : 'pending'}`}>
                            {isFullyApproved ? '✓ Ready / Authorized' : '⏳ In Analysis'}
                          </span>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <button
                            type="button"
                            className="b2b-btn-report-view"
                            onClick={() => handleOpenReport(ord.ledgerTransactionNo)}
                            disabled={reportLoading}
                          >
                            👁️ View Report
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Right: Quick Actions & Financial Navigation */}
        <div className="b2b-card">
          <h3 className="b2b-card-title">Portal Quick Operations</h3>
          <div className="b2b-quick-actions">
            {isPrepaid && (
              <button
                type="button"
                className="b2b-action-item action-highlight"
                onClick={() => {
                  setRechargeMsg(null);
                  setIsRechargeOpen(true);
                }}
              >
                <span>⚡ Instant Quota Top-Up</span>
                <span>&rarr;</span>
              </button>
            )}

            <Link to="/b2b/portal/orders" className="b2b-action-item">
              <span>📋 Search Patient Orders &amp; Results</span>
              <span>&rarr;</span>
            </Link>

            <Link to="/b2b/portal/invoices" className="b2b-action-item">
              <span>🧾 Invoices &amp; Reconciliation</span>
              <span>&rarr;</span>
            </Link>

            <Link to="/b2b/portal/ledger" className="b2b-action-item">
              <span>📜 Download Financial Passbook / Statement</span>
              <span>&rarr;</span>
            </Link>
          </div>

          <div className="b2b-support-box">
            <span className="b2b-support-title">Need Priority Laboratory Support?</span>
            <p className="b2b-support-desc">
              Dedicated B2B Desk: <strong>+91 (011) 4000-8899</strong><br />
              Email: <strong>b2b-support@mylis.enterprise.in</strong>
            </p>
          </div>
        </div>
      </div>

      {/* ── Clinical Report Modal (Hospital-Grade Design) ── */}
      {selectedReport && (
        <div className="b2b-modal-overlay" onClick={() => setSelectedReport(null)}>
          <div className="b2b-modal-card report-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="b2b-report-doc-header">
              <div className="b2b-report-header-top">
                <div>
                  <h2 className="b2b-report-lab-name">MY LIS ENTERPRISE DIAGNOSTICS</h2>
                  <span className="b2b-report-lab-sub">National Reference Laboratory &bull; NABL &amp; CAP Accredited Hub</span>
                </div>
                <button type="button" className="b2b-modal-close" onClick={() => setSelectedReport(null)}>✕</button>
              </div>

              {/* Patient Demographics Box */}
              <div className="b2b-report-patient-box">
                <div className="b2b-rp-row">
                  <div>Patient Name: <strong>{selectedReport.patient?.patientName || selectedReport.patient?.pName}</strong></div>
                  <div>Lab No: <strong className="font-mono">{selectedReport.patient?.ledgerTransactionNo}</strong></div>
                  <div>Booking Date: <strong>{new Date(selectedReport.patient?.bookingDate || Date.now()).toLocaleDateString('en-IN')}</strong></div>
                </div>
                <div className="b2b-rp-row">
                  <div>UHID: <strong className="font-mono">{selectedReport.patient?.uhid || selectedReport.patient?.patient_ID}</strong></div>
                  <div>Age / Gender: <strong>{selectedReport.patient?.ageYear ? `${selectedReport.patient?.ageYear} Y` : '—'} / {selectedReport.patient?.gender || '—'}</strong></div>
                  <div>Ref Doctor: <strong>{selectedReport.patient?.doctorName || 'Self / Hospital'}</strong></div>
                </div>
              </div>
            </div>

            <div className="b2b-report-doc-body">
              <h4 className="b2b-report-sec-title">CLINICAL INVESTIGATIONS &amp; PARAMETER VALUES</h4>
              <table className="b2b-report-table">
                <thead>
                  <tr>
                    <th>Investigation / Test Parameter</th>
                    <th>Tube Barcode</th>
                    <th>Result Status</th>
                    <th>Biological Reference Range</th>
                    <th>Verified By</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedReport.tests && selectedReport.tests.length > 0 ? (
                    selectedReport.tests.map((t, idx) => (
                      <tr key={idx}>
                        <td>
                          <strong>{t.investigationName || t.itemName}</strong>
                        </td>
                        <td className="font-mono" style={{ color: '#0284c7' }}>
                          {t.barcodeNo || 'Allocated'}
                        </td>
                        <td>
                          <span className={`b2b-badge ${t.approved ? 'completed' : 'pending'}`}>
                            {t.approved ? '✓ Verified Normal' : '⏳ In Process'}
                          </span>
                        </td>
                        <td style={{ color: '#64748b' }}>
                          Reference range established on Cobas 6000 / Sysmex
                        </td>
                        <td>
                          <span style={{ fontSize: '12px' }}>{t.approvedBy || 'Pathologist On Duty'}</span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" style={{ textAlign: 'center', padding: '20px' }}>
                        Parameters verified and authorized under laboratory protocol.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              <div className="b2b-report-signature-block">
                <div className="b2b-report-qr-stamp">
                  <div className="b2b-qr-box">QR AUTH</div>
                  <span style={{ fontSize: '10px', color: '#64748b' }}>Digitally Signed &bull; Tamper Evident</span>
                </div>
                <div className="b2b-report-sign-info">
                  <div className="b2b-signature-line" />
                  <strong>Dr. Rajesh K. Varma, MD (Pathology)</strong>
                  <span>Chief of Laboratory Medicine &bull; Reg: MCI-77401</span>
                </div>
              </div>
            </div>

            <div className="b2b-modal-footer">
              <button type="button" className="b2b-btn-cancel" onClick={() => setSelectedReport(null)}>
                Close
              </button>
              <button type="button" className="b2b-btn-print-doc" onClick={() => window.print()}>
                🖨️ Print Clinical Report
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Self-Service Wallet Recharge Modal ── */}
      {isRechargeOpen && (
        <div className="b2b-modal-overlay" onClick={() => !isSubmittingRecharge && setIsRechargeOpen(false)}>
          <div className="b2b-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="b2b-modal-header">
              <h3 className="b2b-modal-title">⚡ Instant Prepaid Quota Top-Up</h3>
              <button
                type="button"
                className="b2b-modal-close"
                onClick={() => setIsRechargeOpen(false)}
                disabled={isSubmittingRecharge}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleRechargeSubmit}>
              <div className="b2b-modal-body">
                {rechargeMsg && (
                  <div className={`b2b-msg-alert ${rechargeMsg.type}`}>
                    {rechargeMsg.text}
                  </div>
                )}

                <div className="b2b-recharge-slabs">
                  <span className="b2b-form-label">Quick Top-Up Slabs:</span>
                  <div className="b2b-slab-buttons">
                    {[5000, 10000, 20000, 50000].map((amt) => (
                      <button
                        type="button"
                        key={amt}
                        className={`b2b-slab-btn ${rechargeAmount === amt ? 'selected' : ''}`}
                        onClick={() => setRechargeAmount(amt)}
                      >
                        ₹{amt.toLocaleString('en-IN')}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="b2b-form-group">
                  <label className="b2b-form-label">Deposit Amount (₹) <span style={{ color: '#ef4444' }}>*</span></label>
                  <input
                    type="number"
                    min="1000"
                    step="500"
                    className="b2b-form-input font-mono"
                    value={rechargeAmount}
                    onChange={(e) => setRechargeAmount(e.target.value)}
                    required
                  />
                </div>

                <div className="b2b-form-group">
                  <label className="b2b-form-label">Payment Channel</label>
                  <div className="b2b-mode-selector">
                    {['UPI', 'NEFT', 'RTGS', 'CARD'].map((m) => (
                      <label key={m} className={`b2b-mode-label ${rechargeMode === m ? 'active' : ''}`}>
                        <input
                          type="radio"
                          name="rechargeMode"
                          value={m}
                          checked={rechargeMode === m}
                          onChange={(e) => setRechargeMode(e.target.value)}
                        />
                        {m === 'UPI' ? '📱 Instant UPI / QR' : m === 'NEFT' ? '🏦 Bank NEFT' : m === 'RTGS' ? '🏛️ RTGS Transfer' : '💳 Corporate Card'}
                      </label>
                    ))}
                  </div>
                </div>

                <div className="b2b-form-group">
                  <label className="b2b-form-label">Bank Reference / UTR Number</label>
                  <input
                    type="text"
                    className="b2b-form-input"
                    placeholder="e.g. UTR / UPI Ref ID (Auto-generated if left blank)"
                    value={rechargeRef}
                    onChange={(e) => setRechargeRef(e.target.value)}
                  />
                </div>
              </div>

              <div className="b2b-modal-footer">
                <button
                  type="button"
                  className="b2b-btn-cancel"
                  onClick={() => setIsRechargeOpen(false)}
                  disabled={isSubmittingRecharge}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="b2b-btn-topup"
                  disabled={isSubmittingRecharge}
                >
                  {isSubmittingRecharge ? 'Processing Advance...' : `Credit ₹${Number(rechargeAmount || 0).toLocaleString('en-IN')} to Wallet`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default B2bPortalDashboardPage;
